if (!customElements.get('product-photo-upload')) {
  customElements.define(
    'product-photo-upload',
    class ProductPhotoUpload extends HTMLElement {
      connectedCallback() {
        this.fileInput = this.querySelector('input[type="file"]');
        this.errorElement = this.querySelector('.product-photo-upload__error');
        this.requiredErrorMessage = this.errorElement?.textContent?.trim() || 'Щоб додати товар у кошик, завантажте фото.';
        this.uploadingMessage = this.dataset.uploadingMessage || 'Дочекайтесь завершення завантаження фото.';
        this.uploadFailedMessage = this.dataset.uploadFailedMessage || 'Виникла помилка при завантаженні файлу.';
        this.noUrlsMessage = this.dataset.noUrlsMessage || 'Проксі не повернув посилання на завантажені файли.';
        this.previewAltTemplate = this.dataset.previewAltTemplate || 'Превʼю [name]';
        this.removeAriaTemplate = this.dataset.removeAriaTemplate || 'Видалити фото [name]';
        this.fileNameTemplate = this.dataset.fileNameTemplate || 'Файл [index]';
        this.dropzoneElement = this.querySelector('.product-photo-upload__dropzone');
        this.chooseButton = this.querySelector('.product-photo-upload__choose');
        this.previewElement = this.querySelector('.product-photo-upload__preview');
        this.previewListElement = this.querySelector('.product-photo-upload__preview-list');
        this.form = this.closest('form');
        this.uploadedItems = [];
        this.previewObjectUrls = [];
        this.proxyUrl = this.dataset.proxyUrl || '/apps/api/upload';
        this.propertyName = this.dataset.propertyName || 'photo_url';
        this.hiddenInputsContainer = document.createElement('div');
        this.hiddenInputsContainer.hidden = true;
        this.hiddenInputsContainer.className = 'product-photo-upload__hidden-inputs';
        this.form?.appendChild(this.hiddenInputsContainer);

        if (!this.fileInput || !this.form) return;

        this.onSubmit = this.handleSubmit.bind(this);
        this.onFileChange = this.handleFileChange.bind(this);
        this.onChooseClick = this.handleChooseClick.bind(this);
        this.onDropzoneClick = this.handleDropzoneClick.bind(this);
        this.onDropzoneKeydown = this.handleDropzoneKeydown.bind(this);
        this.onDragOver = this.handleDragOver.bind(this);
        this.onDragLeave = this.handleDragLeave.bind(this);
        this.onDrop = this.handleDrop.bind(this);
        this.onPreviewClick = this.handlePreviewClick.bind(this);
        this.onProductAdded = this.handleProductAdded.bind(this);
        this.onFormValidate = this.handleFormValidate.bind(this);

        this.form.addEventListener('submit', this.onSubmit);
        this.fileInput.addEventListener('change', this.onFileChange);
        this.chooseButton?.addEventListener('click', this.onChooseClick);
        this.dropzoneElement?.addEventListener('click', this.onDropzoneClick);
        this.dropzoneElement?.addEventListener('keydown', this.onDropzoneKeydown);
        this.dropzoneElement?.addEventListener('dragover', this.onDragOver);
        this.dropzoneElement?.addEventListener('dragleave', this.onDragLeave);
        this.dropzoneElement?.addEventListener('drop', this.onDrop);
        this.previewListElement?.addEventListener('click', this.onPreviewClick);
        document.addEventListener('product-ajax:added', this.onProductAdded);
        this.form.addEventListener('product-form:validate', this.onFormValidate);
        this.clearPreview();
        this.resetValidationState();
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        this.fileInput?.removeEventListener('change', this.onFileChange);
        this.chooseButton?.removeEventListener('click', this.onChooseClick);
        this.dropzoneElement?.removeEventListener('click', this.onDropzoneClick);
        this.dropzoneElement?.removeEventListener('keydown', this.onDropzoneKeydown);
        this.dropzoneElement?.removeEventListener('dragover', this.onDragOver);
        this.dropzoneElement?.removeEventListener('dragleave', this.onDragLeave);
        this.dropzoneElement?.removeEventListener('drop', this.onDrop);
        this.previewListElement?.removeEventListener('click', this.onPreviewClick);
        document.removeEventListener('product-ajax:added', this.onProductAdded);
        this.form?.removeEventListener('product-form:validate', this.onFormValidate);
        this.revokePreviewUrl();
      }

      handleSubmit(event) {
        this.handleFormValidate(event);
      }

      handleFormValidate(event) {
        if (this.dataset.required === 'false') return;
        this.resetValidationState();

        if (this.classList.contains('is-uploading')) {
          event.preventDefault();
          this.setErrorMessage(this.uploadingMessage);
          this.fileInput.focus();
          return;
        }

        if (this.uploadedItems.length > 0) {
          this.awaitingCartAddResult = true;
          this.setValid();
          return;
        }

        event.preventDefault();
        this.setInvalid();
        this.fileInput.focus();
      }

      async handleFileChange() {
        const selectedFiles = Array.from(this.fileInput.files || []);
        if (selectedFiles.length > 0) {
          await this.processNewFiles(selectedFiles);
          return;
        }

        this.resetValidationState();
      }

      handleChooseClick() {
        this.fileInput.click();
      }

      handleDropzoneClick(event) {
        if (event.target.closest('.product-photo-upload__choose')) return;
        this.fileInput.click();
      }

      handleDropzoneKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.fileInput.click();
      }

      handleDragOver(event) {
        event.preventDefault();
        this.dropzoneElement?.classList.add('is-dragover');
      }

      handleDragLeave(event) {
        event.preventDefault();
        if (!this.dropzoneElement?.contains(event.relatedTarget)) {
          this.dropzoneElement?.classList.remove('is-dragover');
        }
      }

      async handleDrop(event) {
        event.preventDefault();
        this.dropzoneElement?.classList.remove('is-dragover');

        const droppedFiles = Array.from(event.dataTransfer?.files || []);
        if (droppedFiles.length === 0) return;

        await this.processNewFiles(droppedFiles);
      }

      handlePreviewClick(event) {
        const button = event.target.closest('.product-photo-upload__remove');
        if (!button) return;

        const index = Number(button.dataset.index);
        if (Number.isNaN(index)) return;

        this.uploadedItems.splice(index, 1);
        this.syncHiddenInputs();
        this.renderPreview();

        if (this.uploadedItems.length === 0) {
          this.resetValidationState();
        }
      }

      handleProductAdded() {
        if (!this.awaitingCartAddResult) return;
        this.awaitingCartAddResult = false;
        this.resetUploadedState();
      }

      setInvalid() {
        this.setErrorMessage(this.requiredErrorMessage);
      }

      setValid() {
        this.errorElement?.setAttribute('hidden', '');
        this.fileInput.removeAttribute('aria-invalid');
      }

      resetValidationState() {
        if (this.errorElement) {
          this.errorElement.textContent = '';
          this.errorElement.setAttribute('hidden', '');
        }
        this.fileInput.removeAttribute('aria-invalid');
      }

      setErrorMessage(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.removeAttribute('hidden');
        this.fileInput.setAttribute('aria-invalid', 'true');
      }

      renderPreview() {
        if (!this.previewElement || !this.previewListElement) return;
        if (this.uploadedItems.length === 0) {
          this.clearPreview();
          return;
        }

        this.revokePreviewUrl();
        this.previewListElement.innerHTML = '';

        this.uploadedItems.forEach((itemData, index) => {
          const item = document.createElement('li');
          item.className = 'product-photo-upload__preview-item';

          const name = document.createElement('p');
          name.className = 'product-photo-upload__preview-name text-sm';
          name.textContent = itemData.name;

          if (itemData.previewUrl) {
            const image = document.createElement('img');
            image.className = 'product-photo-upload__preview-image';
            image.alt = this.applyTemplate(this.previewAltTemplate, { name: itemData.name });
            image.src = itemData.previewUrl;
            item.appendChild(image);
          }

          item.appendChild(name);

          const removeButton = document.createElement('button');
          removeButton.type = 'button';
          removeButton.className = 'product-photo-upload__remove btn btn--plain text-sm';
          removeButton.innerHTML =
            '<svg class="icon product-photo-upload__remove-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
          removeButton.dataset.index = String(index);
          removeButton.setAttribute('aria-label', this.applyTemplate(this.removeAriaTemplate, { name: itemData.name }));
          item.appendChild(removeButton);

          this.previewListElement.appendChild(item);
        });

        this.previewElement.removeAttribute('hidden');
      }

      clearPreview() {
        if (!this.previewElement || !this.previewListElement) return;
        this.revokePreviewUrl();
        this.previewListElement.innerHTML = '';
        this.previewElement.setAttribute('hidden', '');
      }

      revokePreviewUrl() {
        if (this.previewObjectUrls.length === 0) return;
        this.previewObjectUrls.forEach((url) => {
          if (typeof url === 'string' && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
        this.previewObjectUrls = [];
      }

      applyTemplate(template, variables = {}) {
        if (typeof template !== 'string' || !template.trim()) return '';

        return Object.entries(variables).reduce((result, [key, value]) => {
          return result.replaceAll(`[${key}]`, value == null ? '' : String(value));
        }, template);
      }

      async uploadFilesToProxy() {
        this.classList.add('is-uploading');
        this.resetValidationState();
        const fallbackErrorMessage = this.uploadFailedMessage;

        try {
          const filesToUpload = this.pendingFiles || [];
          const urls = [];

          for (const file of filesToUpload) {
            const uploadFormData = new FormData();
            // Some backends expect "file", others "files[]".
            uploadFormData.append('file', file, file.name);
            uploadFormData.append('files[]', file, file.name);

            const response = await fetch(this.proxyUrl, {
              method: 'POST',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: uploadFormData,
            });

            if (!response.ok) {
              let serverMessage = '';
              try {
                const errorPayload = await response.json();
                serverMessage = this.extractErrorMessage(errorPayload);
              } catch (_) {
                // Ignore parse errors and use fallback message.
              }

              throw new Error(serverMessage || fallbackErrorMessage);
            }

            const payload = await response.json();
            const extractedUrls = this.extractUrls(payload);
            if (extractedUrls.length === 0) {
              throw new Error(this.noUrlsMessage);
            }

            urls.push(extractedUrls[0]);
          }

          return urls;
        } catch (error) {
          const message = this.getSafeErrorMessage(error, fallbackErrorMessage);
          this.setErrorMessage(message);
          throw error;
        } finally {
          this.classList.remove('is-uploading');
        }
      }

      extractUrls(payload) {
        if (!payload) return [];
        if (Array.isArray(payload.urls)) return payload.urls.filter(Boolean);
        if (Array.isArray(payload.data?.urls)) return payload.data.urls.filter(Boolean);
        if (typeof payload.file?.file === 'string') return [payload.file.file];
        if (typeof payload.data?.file?.file === 'string') return [payload.data.file.file];
        if (Array.isArray(payload.files)) {
          return payload.files
            .map((file) => file?.url || file?.location || file?.src)
            .filter(Boolean);
        }
        if (typeof payload.url === 'string') return [payload.url];
        return [];
      }

      extractErrorMessage(payload) {
        if (!payload || typeof payload !== 'object') return '';

        if (typeof payload.message === 'string' && payload.message.trim()) {
          return payload.message.trim();
        }

        if (typeof payload.error === 'string' && payload.error.trim()) {
          return payload.error.trim();
        }

        if (typeof payload.data?.message === 'string' && payload.data.message.trim()) {
          return payload.data.message.trim();
        }

        return '';
      }

      getSafeErrorMessage(error, fallbackMessage) {
        if (!(error instanceof Error)) return fallbackMessage;

        const message = error.message?.trim();
        if (!message || message === 'Failed to fetch') return fallbackMessage;

        return message;
      }

      async processNewFiles(newFiles) {
        if (!newFiles.length) return;

        try {
          this.pendingFiles = newFiles;
          const urls = await this.uploadFilesToProxy();

          urls.forEach((url, index) => {
            const file = newFiles[index];
            this.uploadedItems.push({
              name:
                file?.name ||
                this.applyTemplate(this.fileNameTemplate, {
                  index: this.uploadedItems.length + 1,
                }),
              url,
              previewUrl: url,
            });
          });

          this.syncHiddenInputs();
          this.renderPreview();
          this.setValid();
          this.fileInput.value = '';
        } finally {
          this.pendingFiles = null;
        }
      }

      syncHiddenInputs() {
        if (!this.hiddenInputsContainer) return;
        this.hiddenInputsContainer.innerHTML = '';
        this.uploadedItems.forEach((item, index) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${this.propertyName}_${index + 1}]`;
          input.value = item.url;
          this.hiddenInputsContainer.appendChild(input);
        });
      }

      resetUploadedState() {
        this.uploadedItems = [];
        this.pendingFiles = null;
        this.awaitingCartAddResult = false;
        this.syncHiddenInputs();
        this.clearPreview();
        this.resetValidationState();
        this.fileInput.value = '';
      }
    }
  );
}
